import { Controller } from "@hotwired/stimulus"

const URL_PLATFORM_MAP = {
  "bsky.app":             "bluesky",
  "reddit.com":           "reddit",
  "news.ycombinator.com": "hackernews",
}

export default class extends Controller {
  static targets = ["input", "results", "searchIcon", "platformIcon"]
  static values = { searchUrl: String, linkUrl: String, excludeGroup: Number, fromUrl: String }

  #debounce = null

  search() {
    clearTimeout(this.#debounce)
    const q = this.inputTarget.value.trim()
    if (/^https?:\/\//i.test(q)) {
      this.#setIconFromUrl(q)
      this.#debounce = setTimeout(() => this.#fetchFromUrl(q), 400)
    } else {
      this.#resetIcon()
      this.#debounce = setTimeout(() => this.#fetch(), 250)
    }
  }

  async #fetch() {
    const q = this.inputTarget.value.trim()
    if (q.length < 2) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.classList.add("hidden")
      return
    }

    const url = `${this.searchUrlValue}?q=${encodeURIComponent(q)}&exclude_group=${this.excludeGroupValue}`
    const res = await fetch(url, { headers: { Accept: "application/json" } })
    const authors = await res.json()

    if (authors.length === 0) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.classList.add("hidden")
      return
    }

    this.resultsTarget.innerHTML = authors.map(a => this.#authorItem(a)).join("")
    this.resultsTarget.classList.remove("hidden")
  }

  async #fetchFromUrl(url) {
    if (!this.fromUrlValue) return
    this.resultsTarget.innerHTML = `<li class="px-3 py-2.5 text-meta text-ink-slate">Resolving…</li>`
    this.resultsTarget.classList.remove("hidden")

    const res = await fetch(`${this.fromUrlValue}?url=${encodeURIComponent(url)}`, { headers: { Accept: "application/json" } })
    const data = await res.json()

    if (!res.ok || data.error) {
      this.resultsTarget.innerHTML = `<li class="px-3 py-2.5 text-meta text-ink-slate">${data.error || "Could not resolve this URL."}</li>`
      return
    }

    this.resultsTarget.innerHTML = this.#authorItem(data)
    this.resultsTarget.classList.remove("hidden")
  }

  #setIconFromUrl(url) {
    try {
      const host = new URL(url).hostname.replace(/^www\./, "")
      const platform = URL_PLATFORM_MAP[host]
      if (platform) {
        this.#showPlatformIcon(platform)
        return
      }
    } catch { /* ignore */ }
    this.#resetIcon()
  }

  #showPlatformIcon(platformName) {
    if (this.hasSearchIconTarget) this.searchIconTarget.classList.add("hidden")
    this.platformIconTargets.forEach(t => {
      t.classList.toggle("hidden", t.dataset.platform !== platformName)
    })
  }

  #resetIcon() {
    if (this.hasSearchIconTarget) this.searchIconTarget.classList.remove("hidden")
    this.platformIconTargets.forEach(t => t.classList.add("hidden"))
  }

  #authorItem(a) {
    return `
      <li>
        <button
          type="button"
          data-author-id="${a.id}"
          data-action="click->author-link-search#select"
          class="w-full text-left px-3 py-2 text-btn font-medium hover:bg-well flex items-center gap-2">
          <i class="bi ${a.platform_icon_class || "bi-person"} text-ink-slate"></i>
          ${a.display_handle}
          <span class="text-meta text-ink-slate">${a.platform_name || ""}</span>
        </button>
      </li>
    `
  }

  async select(event) {
    const authorId = event.currentTarget.dataset.authorId
    const form = document.createElement("form")
    form.method = "post"
    form.action = this.linkUrlValue

    const csrf = document.querySelector("meta[name=csrf-token]")
    if (csrf) {
      const csrfInput = document.createElement("input")
      csrfInput.type = "hidden"
      csrfInput.name = "authenticity_token"
      csrfInput.value = csrf.content
      form.appendChild(csrfInput)
    }

    const idInput = document.createElement("input")
    idInput.type = "hidden"
    idInput.name = "linked_author_id"
    idInput.value = authorId
    form.appendChild(idInput)

    document.body.appendChild(form)
    form.submit()
  }
}
